/**
 * CSRFService - Handles CSRF token fetching and management
 *
 * Spring Security CSRF Protection:
 * - Backend uses CookieCsrfTokenRepository.withHttpOnlyFalse()
 * - CSRF token is stored in XSRF-TOKEN cookie (accessible to JavaScript)
 * - Frontend must send token in x-xsrf-token header for POST/PUT/PATCH/DELETE
 * - GET requests don't require CSRF token
 */
import { API_BASE_URL } from "../../../config/apiConfig";

const BASE_URL = API_BASE_URL;

const CSRFService = {
  /**
   * Fetch CSRF token from backend
   * This endpoint triggers Spring Security to generate and set the XSRF-TOKEN cookie
   *
   * @returns {Promise<boolean>} True if token was fetched successfully
   */
  fetchCsrfToken: async () => {
    try {
      console.log("[CSRFService] Fetching CSRF token from backend...");

      const response = await fetch(`${BASE_URL}/api/v1/public/csrf/token`, {
        method: "GET",
        credentials: "include", // Include cookies to receive XSRF-TOKEN cookie
      });

      if (response.ok) {
        // CSRF protection may be disabled on the backend (returns 200 with empty body).
        // Guard against JSON parse failure before trying to read the response body.
        let tokenData = null;
        try {
          const text = await response.text();
          if (text && text.trim() && text.trim() !== "null") {
            tokenData = JSON.parse(text);
          }
        } catch (parseErr) {
          console.warn("[CSRFService] Could not parse CSRF token response (CSRF may be disabled on backend)");
        }

        if (tokenData) {
          console.log("[CSRFService] CSRF token fetched successfully");
          console.log("[CSRFService] Token header name:", tokenData.headerName);
          console.log(
            "[CSRFService] Token parameter name:",
            tokenData.parameterName
          );
        } else {
          console.log("[CSRFService] CSRF token endpoint returned no token data (CSRF likely disabled)");
        }

        // The token is automatically set as a cookie by Spring Security
        // No need to manually store it - just verify it exists
        const csrfCookie = document.cookie
          .split("; ")
          .find((row) => row.startsWith("XSRF-TOKEN="));

        if (csrfCookie) {
          console.log("[CSRFService] XSRF-TOKEN cookie verified in browser");
          return true;
        } else {
          console.warn("[CSRFService] XSRF-TOKEN cookie not found after fetch");
          return false;
        }
      } else {
        console.error(
          "[CSRFService] Failed to fetch CSRF token:",
          response.status,
          response.statusText
        );
        return false;
      }
    } catch (error) {
      console.error("[CSRFService] Error fetching CSRF token:", error);
      return false;
    }
  },

  /**
   * Get CSRF token from cookie
   *
   * @returns {string|null} The CSRF token value or null if not found
   */
  getCsrfToken: () => {
    const cookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("XSRF-TOKEN="));

    if (cookie) {
      const token = cookie.split("=")[1];
      return token;
    }

    return null;
  },

  /**
   * Check if CSRF token exists in cookies
   *
   * @returns {boolean} True if token exists
   */
  hasCsrfToken: () => {
    return CSRFService.getCsrfToken() !== null;
  },
};

export default CSRFService;
